import { PALETTE_INFO, PALETTE_KEYS } from "@/model/palette";
import { SYNTAX_KEYS } from "@/model/tokens";
import { EXPORTERS } from "./registry";
import type { ExportContext, Exporter, ExportResult } from "./types";

export interface PromptOptions {
  /** Append the editable theme JSON in a collapsed block. */
  includeThemeJson: boolean;
}

export const DEFAULT_PROMPT_OPTIONS: PromptOptions = { includeThemeJson: true };

const APP_URL = "https://themes.utimura.io";

function paletteLine(ctx: ExportContext): string {
  return PALETTE_KEYS.map((k) => `${k} ${ctx.color(`palette.${k}`)}`).join(" · ");
}

function rolesLine(ctx: ExportContext): string {
  return SYNTAX_KEYS.map((k) => {
    const style = ctx.resolved.styles[k];
    const flags = (["bold", "italic", "underline"] as const).filter((f) => style[f]);
    return `${k} ${ctx.color(`syntax.${k}`)}${flags.length ? ` (${flags.join(", ")})` : ""}`;
  }).join(" · ");
}

function fence(content: string, language: string): string {
  const ticks = content.includes("```") ? "````" : "```";
  return `${ticks}${language}\n${content.trimEnd()}\n${ticks}`;
}

function sectionFor(r: ExportResult, heading = "##"): string {
  const parts: string[] = [];
  const file = r.files[0]?.path ?? "FILE";
  const steps = r.steps.map((s, i) => `${i + 1}. ${s.replace(/\bFILE\b/g, file)}`);
  parts.push(`${heading} Steps\n${steps.join("\n")}`);
  if (r.verify.length)
    parts.push(`${heading} Verify\n${r.verify.map((v) => `- \`${v}\``).join("\n")}`);
  if (r.notes.length) parts.push(`${heading} Notes\n${r.notes.map((n) => `- ${n}`).join("\n")}`);
  for (const f of r.files) {
    parts.push(`${heading} File: \`${f.path}\`\n${fence(f.content, f.language)}`);
  }
  return parts.join("\n\n");
}

function header(ctx: ExportContext, what: string): string {
  const { theme } = ctx;
  return `Configure ${what} with this color scheme: "${theme.name}" (${theme.variant}), designed with ${APP_URL}.

Change only what is needed to apply the colors. Keep the user's other settings, and ask before deleting anything that is not a color setting.`;
}

function footer(ctx: ExportContext, opts: PromptOptions): string {
  const parts = [`## Palette\n${paletteLine(ctx)}`, `## Syntax roles\n${rolesLine(ctx)}`];
  if (opts.includeThemeJson) {
    parts.push(
      `<details><summary>Theme JSON (open ${APP_URL} and drop this in to keep editing)</summary>\n\n${fence(
        JSON.stringify(ctx.theme, null, 2),
        "json",
      )}\n</details>`,
    );
  }
  return parts.join("\n\n");
}

/** One self-contained prompt for one app. */
export function appPrompt(ctx: ExportContext, e: Exporter, opts = DEFAULT_PROMPT_OPTIONS): string {
  const r = e.run(ctx);
  return `${header(ctx, e.label)}\n\n${sectionFor(r)}\n\n${footer(ctx, opts)}\n`;
}

/** One prompt covering all of the user's apps, in dependency order (terminal first). */
export function bundlePrompt(ctx: ExportContext, opts = DEFAULT_PROMPT_OPTIONS): string {
  const apps = EXPORTERS.filter((e) => e.app);
  const sections = apps.map((e) => `## ${e.label}\n\n${sectionFor(e.run(ctx), "###")}`);
  return `${header(ctx, apps.map((e) => e.label).join(", "))}

Work through the apps in order: the Ghostty palette comes first because Cursor Agent, WeeChat and herdr's base theme read the terminal's ANSI colors.

${sections.join("\n\n")}

${footer(ctx, opts)}
`;
}

/** For any other application: the resolved spec plus mapping guidance. */
export function genericPrompt(ctx: ExportContext, opts = DEFAULT_PROMPT_OPTIONS): string {
  const c = ctx.color;
  const spec = {
    name: ctx.theme.name,
    variant: ctx.theme.variant,
    palette: Object.fromEntries(
      PALETTE_KEYS.map((k) => [k, { hex: c(`palette.${k}`), use: PALETTE_INFO[k].hint }]),
    ),
    terminal: {
      background: c("terminal.background"),
      foreground: c("terminal.foreground"),
      cursor: c("terminal.cursor"),
      selection: c("terminal.selectionBg"),
      ansi: Array.from({ length: 16 }, (_, i) => c(`terminal.ansi${i}`)),
    },
    ui: Object.fromEntries(
      [
        "editorBg",
        "editorFg",
        "panelBg",
        "cursorLine",
        "selection",
        "border",
        "accent",
        "statusBg",
        "statusFg",
        "error",
        "warning",
        "info",
        "hint",
        "ok",
        "diffAdd",
        "diffDelete",
      ].map((k) => [k, c(`ui.${k}` as `ui.${string}`)]),
    ),
    syntax: Object.fromEntries(
      SYNTAX_KEYS.map((k) => [k, { fg: c(`syntax.${k}`), ...ctx.resolved.styles[k] }]),
    ),
  };
  return `${`${header(ctx, "<application>")}

## How to map it
1. Find where <application> stores its colors (theme file, config section or settings UI) and create a theme named \`${ctx.appName}\` rather than editing a built-in one.
2. Use \`terminal.background\`/\`terminal.foreground\` for the main surfaces, \`ui.*\` for chrome (panels, borders, selection, status bar) and \`syntax.*\` for code.
3. If it only supports the 16 terminal colors, use the \`terminal.ansi\` indices instead of hex values; if it supports 256 colors but not 24-bit, pick the nearest xterm-256 color.
4. Reload <application> and confirm text stays readable on every background.

## Spec
${fence(JSON.stringify(spec, null, 2), "json")}

${opts.includeThemeJson ? footer(ctx, { includeThemeJson: true }).split("\n\n").slice(2).join("\n\n") : ""}
`.trimEnd()}\n`;
}
